import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@5.4.624/build/pdf.worker.min.mjs';

export interface LTVData {
    id: string;
    startPK: number;
    endPK: number;
    speed: number;
    reason: string;
    startDate: string;
    endDate: string;
    line?: 'Centrale' | 'Balagne';
}

export async function parseLTVFromPDF(file: File): Promise<LTVData[]> {
    console.log("Analyse du PDF LTV...");
    const arrayBuffer = await file.arrayBuffer();

    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const doc = await loadingTask.promise;

    const ltvResults: LTVData[] = [];

    for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
        const page = await doc.getPage(pageNum);
        const content = await page.getTextContent();
        const items: Array<{ str: string, x: number, y: number, w: number }> = [];

        for (const item of content.items as any[]) {
            const s = (item.str || "").trim();
            if (s.length > 0) {
                items.push({
                    str: s,
                    x: item.transform[4],
                    y: item.transform[5],
                    w: item.width || 0
                });
            }
        }

        // Trouver tous les IDs ASS avec leurs coordonnées Y
        // Un ID ASS est sous la forme YYYY/NNN (ex: 2025/366, 2026/146)
        const idMatches: Array<{ id: string, x: number, y: number }> = [];
        items.forEach(it => {
            const m = it.str.match(/\b(\d{4}\/\d{2,4})\b/);
            if (m && it.x < 100) {
                idMatches.push({ id: m[1], x: it.x, y: it.y });
            }
        });

        // Trier les IDs du haut vers le bas (y décroissant dans le repère PDF)
        idMatches.sort((a, b) => b.y - a.y);

        // Détecter la limite inférieure du tableau (signatures / footer)
        let footerY = 0;
        const footerItem = items.find(it => 
            it.y < 300 && (it.str.toUpperCase().includes("RÉDACTEUR") || it.str.toUpperCase().includes("REDACTEUR") || it.str.toUpperCase().includes("SERVICE"))
        );
        if (footerItem) {
            footerY = footerItem.y;
        }

        // Colonnes X constantes pour les tableaux ASS CFC
        // Col 1 : ASS N° (< 95)
        // Col 2 : ZONE (95 <= x < 210)
        // Col 3 : CAUSE (210 <= x < 330)
        // Col 4 : VITESSE (330 <= x < 400)
        // Col 5 : DATE DEBUT (400 <= x < 480)
        // Col 6 : DATE FIN (>= 480)

        idMatches.forEach((ass, idx) => {
            const nextAss = idMatches[idx + 1];
            // La bande Y de cet enregistrement va du haut de l'ASS courant jusqu'à l'ASS suivant
            // On laisse une marge de tolérance de 20px au-dessus
            const topY = ass.y + 25;
            const bottomY = nextAss ? nextAss.y + 25 : (footerY > 0 ? footerY : 0);

            // Filtrer tous les éléments de texte situés dans cette tranche verticale
            const blockItems = items.filter(it => it.y <= topY && it.y > bottomY);

            let zoneTexts: string[] = [];
            let causeTexts: string[] = [];
            let speedTexts: string[] = [];
            let dateStartTexts: string[] = [];
            let dateEndTexts: string[] = [];

            // Trier les éléments du bloc par Y décroissant puis par X
            blockItems.sort((a, b) => b.y - a.y || a.x - b.x);

            blockItems.forEach(it => {
                if (it.str.match(/\b\d{4}\/\d{2,4}\b/) && it.x < 95) {
                    return; // C'est l'identifiant ASS lui-même
                }

                if (it.x >= 80 && it.x < 205) {
                    zoneTexts.push(it.str);
                } else if (it.x >= 205 && it.x < 330) {
                    causeTexts.push(it.str);
                } else if (it.x >= 330 && it.x < 400) {
                    speedTexts.push(it.str);
                } else if (it.x >= 400 && it.x < 480) {
                    dateStartTexts.push(it.str);
                } else if (it.x >= 480) {
                    dateEndTexts.push(it.str);
                }
            });

            const zoneStr = zoneTexts.join(" ");
            const causeStr = causeTexts.join(" ");
            const speedStr = speedTexts.join(" ");
            const dateStartStr = dateStartTexts.join(" ");
            const dateEndStr = dateEndTexts.join(" ");

            // Extraction des PKs
            const pks: number[] = [];
            const pkRegex = /(?:KM|PK)?\s*(\d{1,3})[+,\.](\d{1,3})/gi;
            let pkMatch;
            const cleanZone = zoneStr.replace(/\s+/g, "");
            while ((pkMatch = pkRegex.exec(cleanZone)) !== null) {
                pks.push(parseInt(pkMatch[1]) + parseInt(pkMatch[2]) / 1000);
            }

            if (pks.length === 0) {
                const simplePkRegex = /(?:KM|PK)\s*(\d{1,3})/gi;
                let sMatch;
                while ((sMatch = simplePkRegex.exec(zoneStr)) !== null) {
                    pks.push(parseInt(sMatch[1]));
                }
            }

            // Vitesse
            let speed = 30;
            const cleanSpeedText = speedStr.replace(/\s+/g, " ").trim();
            const speedMatch = cleanSpeedText.match(/(\d{2,3})\s*(?:KM\/H|KMPH)?/i);
            if (speedMatch) {
                speed = parseInt(speedMatch[1]);
            } else if (cleanSpeedText.toLowerCase().includes("marche à vue") || cleanSpeedText.toLowerCase().includes("marche a vue")) {
                speed = 30;
            }

            // Dates
            const convertToISODate = (dStr: string) => {
                if (!dStr) return "";
                const cleanD = dStr.replace(/\s+/g, "");
                if (cleanD.toUpperCase().includes("RESTITUTION")) return "RESTITUTION";
                const parts = cleanD.match(/(\d{2})[\/\.-](\d{2})[\/\.-](\d{4})/);
                if (parts) return `${parts[3]}-${parts[2]}-${parts[1]}`;
                return cleanD;
            };

            const dates = dateStartStr.match(/(\d{2}[\/\.-]\d{2}[\/\.-]\d{4})/g) || [];
            let startDate = convertToISODate(dates[0] || dateStartStr.trim());
            let endDate = convertToISODate(dateEndStr.trim());
            if (!endDate || endDate.toUpperCase().includes("RESTITUTION")) {
                endDate = "RESTITUTION";
            }

            // Motif
            let reason = causeStr.replace(/\s+/g, " ").trim();
            const extraSpeedInfo = cleanSpeedText
                .replace(/^\d{2,3}\s*(?:KM\/H|KMPH)?/i, "")
                .trim();
            if (extraSpeedInfo) {
                reason += (reason ? " - " : "") + extraSpeedInfo;
            }

            if (!reason) {
                reason = "Vigilance Secteur LTV";
            }

            const ltvId = `${ass.id}-${idx}`;

            if (pks.length >= 2) {
                ltvResults.push({
                    id: ltvId,
                    startPK: Math.min(...pks),
                    endPK: Math.max(...pks),
                    speed,
                    reason,
                    startDate,
                    endDate
                });
            } else if (pks.length === 1) {
                ltvResults.push({
                    id: ltvId,
                    startPK: pks[0],
                    endPK: pks[0] + 0.1,
                    speed,
                    reason,
                    startDate,
                    endDate
                });
            }
        });
    }

    console.log("LTVs extraites avec le nouveau parseur: ", ltvResults);
    return ltvResults;
}

export async function parseLTVFromCSV(file: File): Promise<LTVData[]> {
    console.log("Analyse du fichier CSV LTV complexe...");
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                if (!text) return resolve([]);
                
                const rows: string[][] = [];
                let currentRow: string[] = [];
                let currentCell = '';
                let inQuotes = false;
                
                for (let i = 0; i < text.length; i++) {
                    const char = text[i];
                    const nextChar = text[i+1];
                    
                    if (char === '"') {
                        if (inQuotes && nextChar === '"') {
                            currentCell += '"';
                            i++;
                        } else {
                            inQuotes = !inQuotes;
                        }
                    } else if (char === ';' && !inQuotes) {
                        currentRow.push(currentCell.trim());
                        currentCell = '';
                    } else if ((char === '\n' || (char === '\r' && nextChar === '\n')) && !inQuotes) {
                        if (char === '\r') i++;
                        currentRow.push(currentCell.trim());
                        if (currentRow.some(cell => cell !== '')) rows.push(currentRow);
                        currentRow = [];
                        currentCell = '';
                    } else {
                        currentCell += char;
                    }
                }
                
                if (currentCell || currentRow.length > 0) {
                    currentRow.push(currentCell.trim());
                    rows.push(currentRow);
                }

                if (rows.length === 0) return resolve([]);

                const headerStr = rows[0].join(' ').toUpperCase();
                const hasHeader = headerStr.includes('ZONE') || headerStr.includes('ASS');
                const startIndex = hasHeader ? 1 : 0;

                const ltvResults: LTVData[] = [];

                const formatDate = (dStr: string) => {
                    if (!dStr || dStr.toUpperCase().includes('RESTITUTION')) return 'RESTITUTION';
                    const match = dStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                    if (match) return `${match[3]}-${match[2]}-${match[1]}`;
                    const matchIso = dStr.match(/(\d{4})-(\d{2})-(\d{2})/);
                    if (matchIso) return `${matchIso[1]}-${matchIso[2]}-${matchIso[3]}`;
                    return dStr;
                };

                for (let i = startIndex; i < rows.length; i++) {
                    const row = rows[i];
                    if (row.length < 4) continue;

                    const idAss = row[0] || `CSV-${Date.now()}-${i}`;
                    const zoneStr = row[1] || '';
                    const causeStr = row[2] || 'LTV Importée';
                    const vitesseStr = row[3] || '30';
                    const dateDebutStr = row[4] || '';
                    const dateFinStr = row[5] || 'RESTITUTION';

                    const pks: number[] = [];
                    const pkRegex = /KM\s*(\d{2,3})\+(\d{1,3})/gi;
                    let pkMatch;
                    while ((pkMatch = pkRegex.exec(zoneStr)) !== null) {
                        pks.push(parseInt(pkMatch[1]) + parseInt(pkMatch[2]) / 1000);
                    }

                    let speed = 30;
                    const speedMatch = vitesseStr.match(/(\d{2,3})\s*KM\/H/i);
                    if (speedMatch) {
                        speed = parseInt(speedMatch[1]);
                    } else if (vitesseStr.toUpperCase().includes('MARCHE À VUE') || vitesseStr.toUpperCase().includes('MARCHE A VUE')) {
                        speed = 30;
                    } else {
                        const directNumberMatch = vitesseStr.match(/^(\d{2,3})$/);
                        if (directNumberMatch) speed = parseInt(directNumberMatch[1]);
                    }

                    const startDate = formatDate(dateDebutStr);
                    let endDate = formatDate(dateFinStr);

                    const reason = causeStr.replace(/\s+/g, ' ').trim();

                    if (pks.length >= 2 && !isNaN(speed)) {
                        ltvResults.push({
                            id: idAss.replace(/\s/g, ''),
                            startPK: Math.min(...pks),
                            endPK: Math.max(...pks),
                            speed,
                            reason: reason.length > 0 ? reason : 'Vigilance Secteur LTV',
                            startDate,
                            endDate
                        });
                    }
                }
                
                resolve(ltvResults);
            } catch (error) {
                console.error("Erreur parsing CSV: ", error);
                reject(error);
            }
        };
        reader.onerror = () => reject(new Error("Erreur lecture fichier"));
        reader.readAsText(file, "ISO-8859-1");
    });
}
